import { translate, useLocale } from "../../localization";
// components/dashboard/Experience.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Divider,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  getItem,
  getThumbnail,
  uploadThumbnail,
} from "../../services/Content";
import { useExperiences } from "../../context/ExperiencesContext";
import { useAuth } from "../../context/AuthContext";
import { AppButton } from "../ui";
import EditExperienceDialog from "../experiences/EditExperienceDialog";
import JoinCodeDialog from "../JoinCodeDialog";
import usePasteFilesOnHover from "../elementUploads/usePasteFilesOnHover";
import "./Experience.scss";

const THUMBNAIL_INPUT_ACCEPT = "image/png,image/jpeg,image/webp";
const MAX_THUMBNAIL_FILE_BYTES = 6 * 1024 * 1024;

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const separatorIndex = result.indexOf(",");
      const base64 = separatorIndex >= 0 ? result.slice(separatorIndex + 1) : "";

      if (!base64) {
        reject(new Error(translate("The selected image could not be read.")));
        return;
      }

      resolve(base64);
    };
    reader.onerror = () => reject(new Error(translate("The selected image could not be read.")));
    reader.readAsDataURL(file);
  });

const clamp2 = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const Experience = ({ experience, enableThumbnailUpload = false }) => {
  const locale = useLocale();
  const [systemLabel, setSystemLabel] = useState("");
  const [copied, setCopied] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [joinCodeOpen, setJoinCodeOpen] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailDragActive, setThumbnailDragActive] = useState(false);
  const thumbnailInputRef = useRef(null);
  const thumbnailDragDepthRef = useRef(0);

  const { guides } = useExperiences();
  const { permissions } = useAuth();

  const [editOpen, setEditOpen] = useState(false);

  const previewPath = `/viewer/${encodeURIComponent(experience.experienceId)}`;
  const thumbnailId = experience.experienceId || experience.id || "";

  useEffect(() => {
    if (!experience.systemId) {
      setSystemLabel(translate("Unknown system"));
      return;
    }

    getItem("systems", experience.systemId, false)
      .then((response) => setSystemLabel(response?.name || translate("Unknown system")))
      .catch((err) => console.error("Error fetching system label:", err));
  }, [experience.systemId, locale]);

  useEffect(() => {
    let cancelled = false;

    setThumbnailUrl("");
    setThumbnailLoadFailed(false);

    if (!enableThumbnailUpload || !thumbnailId) {
      return () => {
        cancelled = true;
      };
    }

    getThumbnail(thumbnailId).then((url) => {
      if (!cancelled) {
        setThumbnailUrl(url || "");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enableThumbnailUpload, thumbnailId]);

  const guideLabel = guides?.find((g) => g.sk?.includes(experience.guideId))?.name || translate("No guide");

  const copyValue = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((p) => ({ ...p, [key]: true }));
      setSnackbar({
        open: true,
        message: translate("Copied to clipboard"),
        severity: "success",
      });
      setTimeout(() => setCopied((p) => ({ ...p, [key]: false })), 2000);
    } catch (e) {
      console.error("Failed to copy:", e);
    }
  };

  const uploadThumbnailFile = async (file) => {
    if (!THUMBNAIL_INPUT_ACCEPT.split(",").includes(file.type)) {
      setSnackbar({
        open: true,
        message: translate("Choose a PNG, JPEG, or WebP image."),
        severity: "error",
      });
      return;
    }

    if (file.size > MAX_THUMBNAIL_FILE_BYTES) {
      setSnackbar({
        open: true,
        message: translate("Thumbnail images must be 6 MB or smaller."),
        severity: "error",
      });
      return;
    }

    if (!thumbnailId) {
      setSnackbar({
        open: true,
        message: translate("This experience does not have an id for its thumbnail."),
        severity: "error",
      });
      return;
    }

    setThumbnailUploading(true);

    try {
      const image = await readFileAsBase64(file);
      await uploadThumbnail(thumbnailId, image);
      const refreshedUrl = await getThumbnail(thumbnailId);

      setThumbnailLoadFailed(false);
      setThumbnailUrl(refreshedUrl || "");
      setSnackbar({
        open: true,
        message: translate("Experience thumbnail updated."),
        severity: "success",
      });
    } catch (error) {
      console.error("Error uploading experience thumbnail:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          translate("Thumbnail upload failed."),
        severity: "error",
      });
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleThumbnailFiles = (files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;

    if (selectedFiles.length > 1) {
      setSnackbar({
        open: true,
        message: translate("Drop or paste one thumbnail image at a time."),
        severity: "error",
      });
      return;
    }

    uploadThumbnailFile(selectedFiles[0]);
  };

  const handleThumbnailSelected = (event) => {
    handleThumbnailFiles(event.target.files);
    event.target.value = "";
  };

  const thumbnailPasteHandlers = usePasteFilesOnHover({
    enabled: enableThumbnailUpload && Boolean(thumbnailId) && !thumbnailUploading,
    onFiles: handleThumbnailFiles,
  });

  const isThumbnailFileDrag = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleThumbnailDragEnter = (event) => {
    if (!isThumbnailFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();

    if (!thumbnailId || thumbnailUploading) return;

    thumbnailDragDepthRef.current += 1;
    setThumbnailDragActive(true);
  };

  const handleThumbnailDragOver = (event) => {
    if (!isThumbnailFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = thumbnailId && !thumbnailUploading ? "copy" : "none";
  };

  const handleThumbnailDragLeave = (event) => {
    if (!thumbnailDragActive) return;

    event.preventDefault();
    event.stopPropagation();
    thumbnailDragDepthRef.current = Math.max(0, thumbnailDragDepthRef.current - 1);

    if (thumbnailDragDepthRef.current === 0) {
      setThumbnailDragActive(false);
    }
  };

  const handleThumbnailDrop = (event) => {
    if (!isThumbnailFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    thumbnailDragDepthRef.current = 0;
    setThumbnailDragActive(false);

    if (!thumbnailId || thumbnailUploading) return;

    handleThumbnailFiles(event.dataTransfer.files);
    event.dataTransfer.clearData?.();
  };

  return (
    <>
      <Card
        className="experienceCard"
        role="group"
        aria-label={translate("{value0} experience card", {
  value0: experience.name || translate("Experience")
})}
        onDragEnter={enableThumbnailUpload ? handleThumbnailDragEnter : undefined}
        onDragOver={enableThumbnailUpload ? handleThumbnailDragOver : undefined}
        onDragLeave={enableThumbnailUpload ? handleThumbnailDragLeave : undefined}
        onDrop={enableThumbnailUpload ? handleThumbnailDrop : undefined}
        {...(enableThumbnailUpload ? thumbnailPasteHandlers : {})}
        elevation={2}
        sx={{
          borderRadius: 1,
          p: 2.25,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 1.25, md: 2 },
          width: "100%",
          "&:hover": { boxShadow: 4 },
        }}
      >
        {enableThumbnailUpload && thumbnailDragActive && (
          <Box className="experienceCard__thumbnailDropOverlay" role="status">
            <UploadFileIcon aria-hidden="true" />
            <Typography component="span">{translate("Drop to update thumbnail")}</Typography>
          </Box>
        )}

        {enableThumbnailUpload && (
          <Box className="experienceCard__thumbnailColumn">
            <Box className="experienceCard__thumbnailFrame">
              {thumbnailUrl && !thumbnailLoadFailed ? (
                <img
                  className="experienceCard__thumbnailImage"
                  src={thumbnailUrl}
                  alt={translate("{value0} thumbnail", {
  value0: experience.name || translate("Experience")
})}
                  onError={() => setThumbnailLoadFailed(true)}
                />
              ) : (
                <Box
                  className="experienceCard__thumbnailFallback"
                  role="img"
                  aria-label={translate("No experience thumbnail")}
                >
                  <ImageOutlinedIcon aria-hidden="true" />
                </Box>
              )}
            </Box>
            <input
              ref={thumbnailInputRef}
              hidden
              type="file"
              accept={THUMBNAIL_INPUT_ACCEPT}
              aria-label={translate("Choose thumbnail for {value0}", {
  value0: experience.name || "experience"
})}
              onChange={handleThumbnailSelected}
            />
            <AppButton
              className="experienceCard__thumbnailButton"
              kind="ghost"
              size="small"
              startIcon={<UploadFileIcon fontSize="small" />}
              loading={thumbnailUploading}
              loadingLabel={translate("Uploading")}
              disabled={!thumbnailId}
              title={translate("PNG, JPEG, or WebP; maximum 6 MB. You can also drop or paste an image while hovering this card.")}
              onClick={() => thumbnailInputRef.current?.click()}
            >
              {thumbnailUrl && !thumbnailLoadFailed ? translate("Replace") : translate("Upload")}
            </AppButton>
          </Box>
        )}

        {/* Experience */}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>{translate("Experience")}</Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={clamp2}>
            {experience.name}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
            <Chip size="small" variant="outlined" label={translate("ID")} />
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              {experience.experienceId}
            </Typography>
            <Tooltip title={copied.expId ? translate("Copied!") : translate("Copy")}>
              <IconButton size="small" onClick={() => copyValue("expId", experience.experienceId)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }} />

        {/* System */}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>{translate("System")}</Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={clamp2}>
            {systemLabel}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
            <Chip size="small" color="primary" variant="outlined" label={translate("System ID")} />
            <Typography variant="caption" color="text.secondary">
              {experience.systemId}
            </Typography>
            <Tooltip title={copied.sysId ? translate("Copied!") : translate("Copy")}>
              <IconButton size="small" onClick={() => copyValue("sysId", experience.systemId)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }} />

        {/* Guide */}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>{translate("Guide")}</Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={clamp2}>
            {guideLabel}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
            <Chip size="small" color="secondary" variant="outlined" label={translate("Guide ID")} />
            <Typography variant="caption" color="text.secondary">
              {experience.guideId}
            </Typography>
            <Tooltip title={copied.guideId ? translate("Copied!") : translate("Copy")}>
              <IconButton size="small" onClick={() => copyValue("guideId", experience.guideId)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Actions */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ ml: { md: "auto" }, mt: { xs: 0.5, md: 0 } }}
          flexShrink={0}
        >
          <AppButton
            kind="primary"
            size="small"
            startIcon={<OpenInNewIcon fontSize="small" />}
            href={permissions?.viewWebContent === true ? previewPath : "/upgrade"}
            target="_blank"
            rel="noopener noreferrer"
          >
            {permissions?.viewWebContent === true ? translate("Preview") : translate("Upgrade to Preview")}
          </AppButton>
          {permissions?.manageJoinCodes === true && <AppButton
            kind="secondary"
            size="small"
            onClick={() => setJoinCodeOpen(true)}
          >{translate("Join Code")}</AppButton>}
          <AppButton
            kind="ghost"
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => setEditOpen(true)}
          >{translate("Edit")}</AppButton>
        </Stack>
      </Card>

      <EditExperienceDialog
        open={editOpen}
        experience={experience}
        onClose={() => setEditOpen(false)}
      />

      <JoinCodeDialog
        open={joinCodeOpen}
        onClose={() => setJoinCodeOpen(false)}
        experienceId={experience.experienceId}
        experienceName={experience.name}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={1800}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Experience;
